import React, { useState } from "react";
import {
  Box,
  Switch,
  Typography,
  Paper,
  Alert,
  styled,
  TextField,
  Button,
} from "@mui/material";
import { useUser } from "../UserContext";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
}));

const InfoBox = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: "8px",
}));

const ChatBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: "8px",
  minHeight: "200px",
  maxHeight: "400px",
  overflowY: "auto",
}));

const MessageInput = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const RavenAI: React.FC = () => {
  const {} = useUser();
  const [isEnabled, setIsEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = () => {
    setIsEnabled(!isEnabled);
    setError(null);
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    console.log("Starting API call...");
    console.log("API Key available:", !!import.meta.env.VITE_OPENAI_API_KEY);

    setIsLoading(true);
    setError(null);

    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) {
      console.error("API key is missing");
      setError("API key is not configured");
      setIsLoading(false);
      return;
    }

    try {
      console.log("Making fetch request...");
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content:
                "You are a knowledgeable brand assistant that helps with brand information and insights.",
            },
            {
              role: "user",
              content: message,
            },
          ],
          temperature: 0.7,
          max_tokens: 500,
        }),
      });

      console.log("Response received:", res.status);
      const data = await res.json();
      console.log("Response data:", data);

      if (!res.ok) {
        throw new Error(
          data.error?.message || "Failed to get response from OpenAI"
        );
      }

      setResponse(data.choices[0].message.content);
      setMessage("");
    } catch (error) {
      console.error("Detailed error:", error);
      setError(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
      <StyledPaper elevation={3}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Raven AI
        </Typography>

        <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
          Raven AI is a smart assistant that gets trained using your brand
          materials uploaded through this portal. By customizing Raven AI's
          knowledge, you can help prepare your brand ambassadors with the best
          tools and insights before they head out to tastings.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
          <Typography variant="h6">Enable Raven One</Typography>
          <Switch checked={isEnabled} onChange={handleToggle} color="primary" />
        </Box>

        {isEnabled && (
          <>
            <ChatBox>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {response && (
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {response}
                </Typography>
              )}
            </ChatBox>

            <MessageInput>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isLoading}
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={isLoading || !message.trim()}
              >
                {isLoading ? "Sending..." : "Send"}
              </Button>
            </MessageInput>
          </>
        )}

        <InfoBox severity="info">
          <Typography variant="body2">
            By using this service, you acknowledge that Raven One is a paid
            service charged at a rate of 20 cents per inquiry. Charges will be
            billed monthly based on the number of requests, and you will be
            notified of any changes to your usage. Please ensure you understand
            the pricing structure before using the service to avoid unexpected
            charges.
          </Typography>
        </InfoBox>
      </StyledPaper>
    </Box>
  );
};

export default RavenAI;
